import { FiTarget, FiAward, FiStar, FiMapPin } from 'react-icons/fi';

const RecognitionsTimelineSection = () => {
  const timelineEvents = [
    {
      year: '2021',
      title: 'Prison Chess Program Launch',
      description: 'Initiated chess training program for inmates at San Fernando Jail and General Santos City Jail in the Philippines.',
      type: 'milestone',
    },
    {
      year: '2021',
      title: '2nd Intercontinental Chess Championship',
      description: 'Coached Philippine inmates who competed against players from 46 countries in the Intercontinental Championship for Prisoners.',
      type: 'achievement',
    },
    {
      year: '2021',
      title: 'Philippine Senate Recognition',
      description: 'Received official Senate resolution congratulating the prison chess team and their coaches for their international achievement.',
      type: 'recognition',
    },
    {
      year: '2022',
      title: 'FIDE News Feature',
      description: 'Prison chess initiative featured in FIDE (World Chess Federation) official news coverage, reaching international chess community.',
      type: 'recognition',
    },
    {
      year: '2022',
      title: 'General Santos City Jail Certificate',
      description: 'Awarded certificate of appreciation from General Santos City Jail for dedication to inmate rehabilitation through chess.',
      type: 'recognition',
    },
    {
      year: '2023',
      title: 'National Amateur Championship Organizer',
      description: 'Organized and raised funds for the P100,000 National Amateur Chess Championships in Tagaytay, supporting the chess community.',
      type: 'milestone',
    },
    {
      year: '2024',
      title: 'Continued Tournament Support',
      description: 'Organized and fundraised for the Philippine Amateur Chess Championships, continuing commitment to chess development.',
      type: 'milestone',
    },
    {
      year: 'Ongoing',
      title: 'Community Outreach in Kolkata',
      description: 'Conducting chess workshops, educational programs, and animal welfare initiatives in Kolkata communities.',
      type: 'milestone',
    },
  ];

  const getTypeConfig = (type) => {
    switch (type) {
      case 'milestone':
        return {
          color: 'var(--color-accent-6)',
          bgColor: 'var(--color-accent-2)',
          icon: FiTarget,
          label: 'Milestone',
        };
      case 'achievement':
        return {
          color: 'var(--color-accent-7)',
          bgColor: 'var(--color-accent-2)',
          icon: FiAward,
          label: 'Achievement',
        };
      case 'recognition':
        return {
          color: 'var(--color-accent-5)',
          bgColor: 'var(--color-accent-1)',
          icon: FiStar,
          label: 'Recognition',
        };
      default:
        return {
          color: 'var(--color-accent-6)',
          bgColor: 'var(--color-accent-2)',
          icon: FiMapPin,
          label: 'Event',
        };
    }
  };

  return (
    <section
      className="py-16 md:py-24"
      style={{ backgroundColor: 'var(--color-theme-1)' }}
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Journey of Impact
          </h2>
          <p
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: 'var(--color-subheading)' }}
          >
            A timeline of achievements, recognition, and milestones in the mission to use chess for social good
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line - hidden on mobile */}
            <div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 -ml-px"
              style={{ backgroundColor: 'var(--color-accent-3)' }}
            />

            {/* Timeline items */}
            <div className="space-y-12">
              {timelineEvents.map((event, index) => {
                const typeConfig = getTypeConfig(event.type);
                const isEven = index % 2 === 0;

                return (
                  <div
                    key={index}
                    className="relative"
                  >
                    {/* Desktop layout (alternating sides) */}
                    <div className="hidden md:grid md:grid-cols-2 md:gap-8">
                      {/* Left side */}
                      <div className={`${isEven ? '' : 'invisible'}`}>
                        {isEven && (
                          <div className="text-right pr-8">
                            <EventCard event={event} typeConfig={typeConfig} />
                          </div>
                        )}
                      </div>

                      {/* Center dot */}
                      <div className="absolute left-1/2 -ml-4 flex items-center justify-center">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{
                            backgroundColor: typeConfig.bgColor,
                            borderColor: typeConfig.color,
                            borderWidth: '3px',
                            borderStyle: 'solid',
                          }}
                        >
                          {typeConfig.icon && <typeConfig.icon size={16} style={{ color: typeConfig.color }} />}
                        </div>
                      </div>

                      {/* Right side */}
                      <div className={`${!isEven ? '' : 'invisible'}`}>
                        {!isEven && (
                          <div className="pl-8">
                            <EventCard event={event} typeConfig={typeConfig} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mobile layout (single column) */}
                    <div className="md:hidden flex gap-4">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{
                            backgroundColor: typeConfig.bgColor,
                            borderColor: typeConfig.color,
                            borderWidth: '2px',
                            borderStyle: 'solid',
                          }}
                        >
                          {typeConfig.icon && <typeConfig.icon size={20} style={{ color: typeConfig.color }} />}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <EventCard event={event} typeConfig={typeConfig} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <div
            className="p-8 rounded-lg"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-accent-4)',
              borderWidth: '2px',
              borderStyle: 'solid',
            }}
          >
            <h3
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--color-heading)' }}
            >
              The Journey Continues
            </h3>
            <p
              className="text-lg leading-relaxed"
              style={{ color: 'var(--color-card-text)' }}
            >
              Every initiative, every workshop, and every life touched is a step toward a more compassionate world.
              This is not just about chess—it's about using my skills to create meaningful change.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Event Card Component
const EventCard = ({ event, typeConfig }) => {
  return (
    <div
      className="p-6 rounded-lg transition-all duration-200 hover:shadow-lg"
      style={{
        backgroundColor: 'var(--color-card-bg)',
        borderColor: 'var(--color-card-border)',
        borderWidth: '1px',
        borderStyle: 'solid',
      }}
    >
      {/* Year and Type Badge */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span
          className="text-2xl font-bold"
          style={{ color: typeConfig.color }}
        >
          {event.year}
        </span>
        <span
          className="text-xs font-semibold px-3 py-1 rounded-full"
          style={{
            backgroundColor: typeConfig.bgColor,
            color: typeConfig.color,
          }}
        >
          {typeConfig.label}
        </span>
      </div>

      {/* Title */}
      <h4
        className="text-xl font-semibold mb-2"
        style={{ color: 'var(--color-heading)' }}
      >
        {event.title}
      </h4>

      {/* Description */}
      <p
        className="text-base leading-relaxed"
        style={{ color: 'var(--color-card-text)' }}
      >
        {event.description}
      </p>
    </div>
  );
};

export default RecognitionsTimelineSection;
